import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { Treatment } from '@/lib/types';
import { ntd } from '@/lib/format';
import {
  Banner,
  Card,
  Empty,
  Field,
  PageHeader,
  Stat,
  Tag,
  cellStyle,
  inputStyle,
  thStyle,
  useToast,
} from '@/components/ui';
import { Select } from '@/components/Select';
import { useAuth } from '@/store/auth';

type Basis = 'PERFORM' | 'SALE';
interface CommissionRule {
  id: string;
  name: string;
  basis: Basis;
  role: string;
  treatmentId: string | null;
  pct: number;
  active: boolean;
}
interface PreviewRow {
  staffId: string;
  staffName: string;
  staffRole: string;
  baseCents: number;
  bonusCents: number;
  itemCount: number;
}
interface PreviewResp {
  period: string;
  rows: PreviewRow[];
  totals: { staffCount: number; baseCents: number; bonusCents: number };
}
interface Statement extends PreviewRow {
  period: string;
  settledAt: string;
}

const ROLES: { value: string; label: string }[] = [
  { value: 'DOCTOR', label: '醫師' },
  { value: 'CONSULTANT', label: '諮詢師' },
  { value: 'ESTHETICIAN', label: '美容師' },
  { value: 'FRONT_DESK', label: '櫃檯' },
];
const roleLabel = (r: string) => ROLES.find((x) => x.value === r)?.label ?? r;
const BASES: { value: Basis; label: string }[] = [
  { value: 'PERFORM', label: '執行業績(歸屬主治)' },
  { value: 'SALE', label: '銷售業績(歸屬開單者)' },
];
const basisLabel = (b: string) => BASES.find((x) => x.value === b)?.label ?? b;
const thisMonth = () => new Date().toISOString().slice(0, 7);

export function CommissionPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === 'TENANT_ADMIN' || user?.role === 'STORE_MANAGER';
  const { msg, ok, fail } = useToast();
  const [period, setPeriod] = useState(thisMonth());
  const [form, setForm] = useState<{
    name: string;
    basis: Basis;
    role: string;
    treatmentId: string;
    pct: string;
  }>({ name: '', basis: 'PERFORM', role: 'DOCTOR', treatmentId: '', pct: '' });

  const rules = useQuery({
    queryKey: ['commission-rules'],
    queryFn: () => api.get<CommissionRule[]>('/commission/rules'),
    enabled: isAdmin,
  });
  const treatments = useQuery({
    queryKey: ['treatments'],
    queryFn: () => api.get<Treatment[]>('/catalog/treatments'),
    enabled: isAdmin,
  });
  const preview = useQuery({
    queryKey: ['commission-preview', period],
    queryFn: () => api.get<PreviewResp>(`/commission/preview?period=${period}`),
    enabled: isAdmin && /^\d{4}-\d{2}$/.test(period),
  });
  const statements = useQuery({
    queryKey: ['commission-statements', period],
    queryFn: () => api.get<Statement[]>(`/commission/statements?period=${period}`),
    enabled: isAdmin && /^\d{4}-\d{2}$/.test(period),
  });

  const txName = (id: string | null) =>
    !id ? '所有療程' : treatments.data?.find((t) => t.id === id)?.name ?? id;

  const createRule = useMutation({
    mutationFn: () =>
      api.post('/commission/rules', {
        name: form.name.trim(),
        basis: form.basis,
        role: form.role,
        treatmentId: form.treatmentId || undefined,
        pct: Number(form.pct),
      }),
    onSuccess: () => {
      ok('抽成規則已新增');
      setForm({ name: '', basis: 'PERFORM', role: 'DOCTOR', treatmentId: '', pct: '' });
      void qc.invalidateQueries({ queryKey: ['commission-rules'] });
      void qc.invalidateQueries({ queryKey: ['commission-preview'] });
    },
    onError: fail,
  });
  const toggleRule = useMutation({
    mutationFn: (id: string) => api.patch(`/commission/rules/${id}/toggle`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['commission-rules'] });
      void qc.invalidateQueries({ queryKey: ['commission-preview'] });
    },
    onError: fail,
  });
  const settle = useMutation({
    mutationFn: () => api.post<{ settled: number; bonusCents: number }>('/commission/settle', { period }),
    onSuccess: (r) => {
      ok(`已完成 ${period} 月結:${r.settled} 位員工,應發 ${ntd(r.bonusCents)}`);
      void qc.invalidateQueries({ queryKey: ['commission-statements', period] });
    },
    onError: fail,
  });

  if (!isAdmin) {
    return (
      <div>
        <PageHeader title="員工業績獎金" subtitle="抽成規則 · 月結" />
        <Card>
          <Empty>🔒 員工獎金屬薪酬機密,僅租戶管理者可檢視。</Empty>
        </Card>
      </div>
    );
  }

  const canAdd = form.name.trim() && form.pct !== '' && Number(form.pct) >= 0 && Number(form.pct) <= 100;
  const t = preview.data?.totals;

  return (
    <div>
      <PageHeader
        title="員工業績獎金"
        subtitle="依抽成規則計算各員工業績與應發獎金,逐月結算"
        right={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="month"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              style={{ ...inputStyle, width: 150 }}
            />
            <button
              className="btn primary"
              disabled={settle.isPending || !preview.data?.rows.length}
              onClick={() => settle.mutate()}
            >
              執行 {period} 月結
            </button>
          </div>
        }
      />
      <Banner msg={msg} />

      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        <Stat label="期別" value={period} />
        <Stat label="入帳員工" value={t?.staffCount ?? 0} hint="本月有獎金歸屬者" />
        <Stat label="業績基底" value={ntd(t?.baseCents ?? 0)} hint="歸屬療程定價合計" />
        <Stat label="應發獎金" value={ntd(t?.bonusCents ?? 0)} tone="ticket" hint="預覽合計" />
      </div>

      {/* ── 抽成規則 ── */}
      <Card title="抽成規則" icon="⚙️">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1.4fr 1.6fr 1fr 1.4fr 0.7fr auto',
            gap: 8,
            alignItems: 'end',
            marginBottom: 14,
          }}
        >
          <Field label="規則名稱">
            <input
              style={inputStyle}
              placeholder="如:醫師雷射執行"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </Field>
          <Field label="業績類型">
            <Select
              value={form.basis}
              onChange={(v) => setForm({ ...form, basis: v as Basis })}
              options={BASES}
            />
          </Field>
          <Field label="適用角色">
            <Select
              value={form.role}
              onChange={(v) => setForm({ ...form, role: v })}
              options={ROLES}
            />
          </Field>
          <Field label="療程範圍">
            <Select
              value={form.treatmentId}
              onChange={(v) => setForm({ ...form, treatmentId: v })}
              placeholder="所有療程"
              options={[
                { value: '', label: '所有療程' },
                ...(treatments.data ?? []).map((tx) => ({ value: tx.id, label: tx.name })),
              ]}
            />
          </Field>
          <Field label="抽成 %">
            <input
              style={inputStyle}
              type="number"
              min={0}
              max={100}
              placeholder="10"
              value={form.pct}
              onChange={(e) => setForm({ ...form, pct: e.target.value })}
            />
          </Field>
          <button
            className="btn primary"
            disabled={!canAdd || createRule.isPending}
            onClick={() => createRule.mutate()}
          >
            新增
          </button>
        </div>

        {!rules.data?.length ? (
          <Empty>尚未設定抽成規則。先新增規則,系統才會計算獎金。</Empty>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>規則</th>
                <th style={thStyle}>業績類型</th>
                <th style={thStyle}>角色</th>
                <th style={thStyle}>療程範圍</th>
                <th style={thStyle}>抽成</th>
                <th style={thStyle}>狀態</th>
                <th style={thStyle}></th>
              </tr>
            </thead>
            <tbody>
              {rules.data.map((r) => (
                <tr key={r.id} style={{ opacity: r.active ? 1 : 0.5 }}>
                  <td style={cellStyle}>{r.name}</td>
                  <td style={cellStyle}>{basisLabel(r.basis)}</td>
                  <td style={cellStyle}>
                    <Tag>{roleLabel(r.role)}</Tag>
                  </td>
                  <td style={cellStyle}>{txName(r.treatmentId)}</td>
                  <td style={cellStyle} className="num">
                    {r.pct}%
                  </td>
                  <td style={cellStyle}>
                    <Tag tone={r.active ? 'success' : 'default'}>{r.active ? '啟用' : '停用'}</Tag>
                  </td>
                  <td style={cellStyle}>
                    <button className="btn ghost" onClick={() => toggleRule.mutate(r.id)}>
                      {r.active ? '停用' : '啟用'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* ── 本月業績預覽 ── */}
      <Card title={`${period} 業績與獎金預覽`} icon="📊">
        {!preview.data?.rows.length ? (
          <Empty>本月無已完工且符合規則的業績。</Empty>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>員工</th>
                <th style={thStyle}>角色</th>
                <th style={thStyle}>件數</th>
                <th style={thStyle}>業績基底</th>
                <th style={thStyle}>應發獎金</th>
              </tr>
            </thead>
            <tbody>
              {preview.data.rows.map((r) => (
                <tr key={r.staffId}>
                  <td style={cellStyle}>{r.staffName}</td>
                  <td style={cellStyle}>
                    <Tag>{roleLabel(r.staffRole)}</Tag>
                  </td>
                  <td style={cellStyle} className="num">
                    {r.itemCount}
                  </td>
                  <td style={cellStyle} className="num">
                    {ntd(r.baseCents)}
                  </td>
                  <td className="num" style={{ ...cellStyle, fontWeight: 700, color: '#B8860B' }}>
                    {ntd(r.bonusCents)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* ── 已結算 ── */}
      {!!statements.data?.length && (
        <Card title={`${period} 已結算月結快照`} icon="🔒">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>員工</th>
                <th style={thStyle}>角色</th>
                <th style={thStyle}>件數</th>
                <th style={thStyle}>應發獎金</th>
                <th style={thStyle}>結算時間</th>
              </tr>
            </thead>
            <tbody>
              {statements.data.map((s) => (
                <tr key={s.staffId}>
                  <td style={cellStyle}>{s.staffName}</td>
                  <td style={cellStyle}>
                    <Tag>{roleLabel(s.staffRole)}</Tag>
                  </td>
                  <td style={cellStyle} className="num">
                    {s.itemCount}
                  </td>
                  <td style={cellStyle} className="num">
                    {ntd(s.bonusCents)}
                  </td>
                  <td style={cellStyle}>{new Date(s.settledAt).toLocaleString('zh-TW')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
